<template>
    <div class="page">
        <kt-datatable
            :model="workOrderModel"
            :data="rows"
            :filter="datatableFilter"
            :meta="{ tableClass: props.embedded ? '-table-bordered table-sm' : '-table-bordered', rowClass: (row) => getRowClass(row) }"
            :update-function="handleDatatableUpdate"
            :hide-search="props.embedded"
            :storage-key="props.embedded ? 'workorder-embedded' : 'workorder-list'"
        >
            <template #header_select="scope">
                <div class="form-check">
                    <input 
                        class="form-check-input" 
                        type="checkbox" 
                        :checked="selectAll"
                        @change="toggleSelectAll"
                        @click.stop
                        title="Select all on this page"
                    />
                </div>
            </template>
            
            <template #view_select="scope">
                <div class="form-check">
                    <input 
                        class="form-check-input" 
                        type="checkbox" 
                        :checked="selectedRows.includes(scope.row.Id)"
                        @change="toggleRowSelection(scope.row.Id)"
                        @click.stop
                    />
                </div>
            </template>
            
            <template #view_actions="scope">
                <kt-dropmenu v-model:visible="actionDropdown['row_' + scope.row.Id]">
                    <template #button>
                        <button
                            class="btn btn-outline-primary btn-sm"
                            @click="actionDropdown['row_' + scope.row.Id] = !actionDropdown['row_' + scope.row.Id]"
                            :disabled="props.disableActions"
                        >
                            <i class="fas fa-ellipsis-v"></i>
                        </button>
                    </template>
                    <template #dropmenu>
                        <ul class="dropdown-menu show">
                            <li v-if="true || (scope.row.Items && scope.row.Items.length > 0)">
                                <label class="dropdown-item">
                                    <button 
                                        class="btn btn-sm btn-outline-info form-control" 
                                        @click="printInvoice(scope.row)"
                                    >
                                        <i class="fa fa-print"></i> Print
                                    </button>
                                </label>
                            </li>
                            <li>
                                <label class="dropdown-item">
                                    <button 
                                        class="btn btn-sm btn-outline-primary form-control" 
                                        @click="editRecord(scope.row.Id)"
                                    >
                                        <i class="fa fa-edit"></i> Edit
                                    </button>
                                </label>
                            </li>
                            <li>
                                <label class="dropdown-item">
                                    <button 
                                        class="btn btn-sm btn-outline-danger form-control" 
                                        @click="deleteRecord(scope.row.Id)"
                                    >
                                        <i class="fa fa-trash"></i> Delete
                                    </button>
                                </label>
                            </li>
                        </ul>
                    </template>
                </kt-dropmenu>
            </template>

            <template #filters="scope" v-if="!props.hideFilters">
                <div class="col">
                    <div class="row">
                        <div class="col-auto">
                            <client-multi-check 
                                label="Clients"
                                :model-value="currentFilter.client"
                                @update:model-value="(value) => setFilter({client: value})"
                            ></client-multi-check>
                        </div>
                        <div class="col-auto">
                            <work-order-status-multi-check
                                label="Status"
                                :model-value="currentFilter.status"
                                @update:model-value="(value) => setFilter({status: value})"
                            ></work-order-status-multi-check>
                        </div>
                        <div class="col-auto">
                            <kt-date-range-select
                                label="Date Range"
                                v-model="dateRange"
                            ></kt-date-range-select>
                        </div>
                    </div>
                </div>
            </template>

            <template #after-search="scope">
                <div class="btn-group">
                    <button @click="refresh()" class="btn btn-sm btn-outline-secondary" :disabled="props.disableActions">
                        Refresh <i class="fa fa-sync"></i>
                    </button>
                    <button @click="newRecord()" class="btn btn-sm btn-outline-primary" :disabled="props.disableActions">
                        New <i class="fa fa-plus"></i>
                    </button>
                </div>
            </template>

            <template #view_string="scope">
                <span v-if="scope.column.name === 'Contact'">
                    <a href="javascript:void(0);" @click="editContact(scope.row.Contact?.Id)">
                        {{ scope.row.Contact?.Company || 'N/A' }}
                    </a>
                </span>
                <span v-else-if="scope.column.name === 'InvoiceNumber'">
                    <strong>{{ scope.row.InvoiceNumber || '--' }}</strong>
                </span>
                <span v-else-if="scope.column.name === 'Total'">
                    <strong>${{ (scope.row.Total || scope.row.CalculatedTotal || 0).toFixed(2) }}</strong>
                </span>
                <span v-else-if="scope.column.name === 'Status'">
                    <span :class="getStatusBadgeClass(scope.row.Status)" class="badge">
                        {{ getStatusLabel(scope.row.Status) }}
                    </span>
                </span>
                <span v-else-if="scope.column.name === 'Title'">
                    <a href="javascript:void(0);" @click="editRecord(scope.row.Id)">
                        {{ scope.row.Title }}
                    </a>
                </span>
                <span v-else-if="scope.column.name === 'Id'">
                    <a href="javascript:void(0);" @click="editRecord(scope.row.Id)">
                        {{ scope.row.Id }}
                    </a>
                </span>
                <span v-else>
                    {{ scope.value }}
                </span>
            </template>

            <!-- Filter badges in under-header slot -->
            <template #under-header>
                <!-- Bulk Actions Toolbar -->
                <div v-if="selectedRows.length > 0" class="mb-0 p-2 actions-row">
                    <div class="d-flex align-items-center gap-2">
                        <span class="fw-bold text-primary">{{ selectedRows.length }} selected</span>
                        <button 
                            class="btn btn-sm btn-outline-danger"
                            @click="showDeleteModal = true"
                            title="Delete Selected"
                        >
                            <i class="fas fa-trash"></i> Delete
                        </button>
                        <button 
                            class="btn btn-sm btn-outline-secondary me-auto"
                            @click="clearSelection"
                        >
                            Clear Selection
                        </button>
                    </div>
                </div>
                
                <div v-if="hasActiveFilters" class="d-flex flex-wrap gap-2 p-2">
                    <span class="text-muted small me-2">Active filters:</span>
                    
                    <!-- Client badges -->
                    <span v-for="clientId in currentFilter.client" :key="'client-' + clientId" 
                          class="badge bg-primary d-flex align-items-center">
                        Client: {{ getClientName(clientId) }}
                        <button type="button" class="btn-close btn-close-white ms-2" 
                                @click="removeClientFilter(clientId)"
                                style="font-size: 0.7em;"></button>
                    </span>
                    
                    <!-- Status badges -->
                    <span v-for="statusId in currentFilter.status" :key="'status-' + statusId"
                          class="badge bg-warning text-dark d-flex align-items-center">
                        Status: {{ getStatusName(statusId) }}
                        <button type="button" class="btn-close ms-2" 
                                @click="removeStatusFilter(statusId)"
                                style="font-size: 0.7em;"></button>
                    </span>
                    
                    <!-- Date range badge -->
                    <span v-if="currentFilter.startDate || currentFilter.endDate"
                          class="badge bg-info d-flex align-items-center">
                        Date: {{ formatDateRange() }}
                        <button type="button" class="btn-close btn-close-white ms-2" 
                                @click="removeDateFilter()"
                                style="font-size: 0.7em;"></button>
                    </span>
                    
                    <!-- Clear all filters -->
                    <button v-if="hasActiveFilters" 
                            class="btn btn-sm btn-outline-secondary ms-2" 
                            @click="clearAllFilters()">
                        Clear All
                    </button>
                </div>
            </template>
        </kt-datatable>
        
        <!-- Bulk Delete Confirmation Modal -->
        <kt-modal
            :visible="showDeleteModal"
            title="Confirm Bulk Delete"
            @dismiss="showDeleteModal = false"
        >
            <template #body>
                <div class="container-fluid">
                    <p class="text-danger mb-3">
                        <i class="fas fa-exclamation-triangle"></i>
                        Are you sure you want to delete {{ selectedRows.length }} work order(s)? This action cannot be undone.
                    </p>
                </div>
            </template>
            <template #footer>
                <button class="btn btn-secondary" @click="showDeleteModal = false">Cancel</button>
                <button class="btn btn-danger" @click="confirmBulkDelete">Delete</button>
            </template>
        </kt-modal>
    </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import Api from "@/services/ApiService";
import { ApiHttpResponse } from "@/services/Http";
import InvoicePdfService from "@/services/InvoicePdfService";
import { WorkOrderModel, WorkOrderFilter } from "@/models/WorkOrderModel";
import Alert from "@/services/Alert";
import Emitter from "@/services/Emitter";
import KtDatatable from "@/components/UI/KtDatatable.vue";
import KtDropmenu from "@/components/UI/KtDropmenu.vue";
import KtModal from "@/components/UI/KtModal.vue";
import ClientMultiCheck from "@/components/Clients/ClientMultiCheck.vue";
import WorkOrderStatusMultiCheck from "@/components/WorkOrders/WorkOrderStatusMultiCheck.vue";
import KtDateRangeSelect from "@/components/UI/KtDateRangeSelect.vue";
import Cache from "@/services/Cache";
import { useClientStore } from '@/stores/ClientStore';
import { storeToRefs } from 'pinia';

const props = defineProps({
    requestId: {
        type: [Number, String],
        default: null
    },
    clientId: {
        type: [Number, String],
        default: null
    },
    hideFilters: {
        type: Boolean,
        default: false
    },
    embedded: {
        type: Boolean,
        default: false
    },
    disableActions: {
        type: Boolean,
        default: false
    }
});

const router = useRouter();
const route = useRoute();
const rows = ref<WorkOrderModel[]>([]);
const datatableFilter = ref<any>({});
const actionDropdown = ref<Record<string, boolean>>({});

// Load client store for filter badge display
const clientStore = useClientStore();
const { list: ClientList } = storeToRefs(clientStore);
clientStore.reload();

// Filter state interface
interface WorkOrderListFilter {
    client: number[];
    status: number[];
    startDate: string;
    endDate: string;
    page: number;
    size: number;
    sort: any[];
}

// Current filter state (reactive ref)
const currentFilter = ref<WorkOrderListFilter>({
    client: [],
    status: [],
    startDate: '',
    endDate: '',
    page: 0,
    size: 25,
    sort: []
});

// Date range picker (derived from currentFilter)
const dateRange = computed({
    get: () => ({
        startDate: currentFilter.value.startDate || null,
        endDate: currentFilter.value.endDate || null
    }),
    set: (value) => {
        setFilter({
            startDate: value?.startDate || null,
            endDate: value?.endDate || null
        });
    }
});

// Selection state
const selectedRows = ref<number[]>([]);
const selectAll = ref(false);
const showDeleteModal = ref(false);

const statusOptions = [
    { label: 'Draft', value: 0 },
    { label: 'Pending', value: 1 },
    { label: 'Approved', value: 2 },
    { label: 'Completed', value: 3 },
    { label: 'Cancelled', value: 4 }
];

const workOrderModel = [
    {
        name: "Select",
        label: "Select",
        type: "select",
        width: 60,
        locked: true,
        draggable: false
    },
    /*
    {
        name: "Actions",
        label: "Actions",
        type: "actions",
        overflow: true
    },
    */
    {
        name: "Id",
        label: "ID",
        display: true,
        sortable: true
    },
    {
        name: "InvoiceNumber",
        label: "Invoice #",
        display: true,
        sortable: true
    },
    {
        name: "Title",
        label: "Title",
        display: true,
        sortable: true
    },
    {
        name: "Contact",
        label: "Client",
        display: true,
        type: "string"
    },
    {
        name: "DateCreated",
        label: "Created",
        display: true,
        type: "date",
        sortable: true
    },
    {
        name: "DateDue",
        label: "Due Date",
        display: true,
        type: "date"
    },
    {
        name: "Total",
        label: "Total",
        display: true,
        type: "string"
    },
    {
        name: "Status",
        label: "Status",
        display: true,
        type: "string"
    },
    {
        name: "PaymentStatus",
        label: "Payment",
        display: false
    },
    {
        name: "PONumber",
        label: "PO #",
        display: false
    }
];

const handleDatatableUpdate = async (filter: any) => {
    try {
        const params: WorkOrderFilter = {
            ...filter,
            IncludeContact: true,
            IncludeItems: true
        };

        // Apply custom filters
        if (currentFilter.value.client && currentFilter.value.client.length > 0) {
            params.ContactId = currentFilter.value.client;
        }

        if (currentFilter.value.status && currentFilter.value.status.length > 0) {
            params.Status = currentFilter.value.status;
        }

        if (currentFilter.value.startDate) {
            params.StartDate = new Date(currentFilter.value.startDate);
        }

        if (currentFilter.value.endDate) {
            params.EndDate = new Date(currentFilter.value.endDate);
        }

        // If requestId prop is provided, filter by that request only
        if (props.requestId) {
            const requestIdNum = typeof props.requestId === 'string' ? parseInt(props.requestId) : props.requestId;
            params.RequestId = [requestIdNum];
        }

        if (props.clientId) {
            const clientIdNum = typeof props.clientId === 'string' ? parseInt(props.clientId) : props.clientId;
            params.ContactId = [clientIdNum];
        }

        console.debug("all params", params);

        const response: ApiHttpResponse<WorkOrderModel[]> = await Api.loadWorkOrders(params);

        

        rows.value = response.Data || [];
        datatableFilter.value = response.Meta?.Filter || {};
        
        return {
            data: rows.value,
            total: response.Meta?.Total || 0
        };
    } catch (error) {
        console.error('Failed to load work orders:', error);
        Alert.notify({ text: 'Failed to load work orders', type: 'error' });
        return { data: [], total: 0 };
    }
};

// Parse URL params to filter object
const parseUrlToFilter = (): WorkOrderListFilter => {
    const routeQuery = route.query;
    
    return {
        client: routeQuery.client ? String(routeQuery.client).split(',').map(Number).filter(n => !isNaN(n)) : [],
        status: routeQuery.status ? String(routeQuery.status).split(',').map(Number).filter(n => !isNaN(n)) : [],
        startDate: String(routeQuery.startDate || ''),
        endDate: String(routeQuery.endDate || ''),
        page: routeQuery.page ? parseInt(String(routeQuery.page)) : 0,
        size: routeQuery.size ? parseInt(String(routeQuery.size)) : 25,
        sort: []
    };
};

// Build URL params from filter object
const buildUrlFromFilter = (filter: WorkOrderListFilter): string => {
    const params = new URLSearchParams();
    
    // Add array parameters
    if (filter.client && filter.client.length > 0) params.set('client', filter.client.join(','));
    if (filter.status && filter.status.length > 0) params.set('status', filter.status.join(','));
    
    // Add date parameters
    if (filter.startDate) params.set('startDate', filter.startDate);
    if (filter.endDate) params.set('endDate', filter.endDate);
    
    // Add pagination parameters
    if (filter.page && filter.page > 0) params.set('page', filter.page.toString());
    if (filter.size && filter.size !== 25) params.set('size', filter.size.toString());
    
    return params.toString();
};

// Load filter from Cache service as fallback
const loadFilterFromStorage = (): WorkOrderListFilter => {
    try {
        const stored = Cache.get('workorder-list-filter');
        if (stored) {
            return stored;
        }
    } catch (e) {
        console.error('Failed to load filter from storage:', e);
    }
    // Return defaults
    return {
        client: [],
        status: [],
        startDate: '',
        endDate: '',
        page: 0,
        size: 25,
        sort: []
    };
};

// Initialize filter from URL or localStorage
const initializeFilter = () => {
    if (props.embedded) {
        // Embedded mode doesn't use URL params
        return;
    }
    
    // Priority: URL params > localStorage > defaults
    const urlFilter = parseUrlToFilter();
    const hasUrlParams = window.location.search.length > 0;
    
    if (hasUrlParams) {
        // URL has params, use them
        currentFilter.value = urlFilter;
    } else {
        // No URL params, try localStorage
        currentFilter.value = loadFilterFromStorage();
    }
    
    console.debug('Initialized filter:', currentFilter.value);
};

// Updated setFilter for URL-based routing (like Job list)
const setFilter = (filterUpdate: any) => {
    console.log('WorkOrderDatatable - setFilter called with:', filterUpdate);
    
    if (props.embedded) {
        // Embedded mode: just update filter and refresh
        Object.assign(currentFilter.value, filterUpdate);
        refresh();
        return;
    }
    
    // Create a temporary filter object for URL building
    const tempFilter = { ...currentFilter.value };
    
    // Update temp filter with new data
    if (filterUpdate.client !== undefined) {
        tempFilter.client = Array.isArray(filterUpdate.client) ? filterUpdate.client : (filterUpdate.client ? [filterUpdate.client] : []);
    }
    if (filterUpdate.status !== undefined) {
        tempFilter.status = Array.isArray(filterUpdate.status) ? filterUpdate.status : (filterUpdate.status ? [filterUpdate.status] : []);
    }
    if (filterUpdate.startDate !== undefined) tempFilter.startDate = filterUpdate.startDate || '';
    if (filterUpdate.endDate !== undefined) tempFilter.endDate = filterUpdate.endDate || '';
    
    // Reset to first page when filter changes
    tempFilter.page = 0;
    
    // Build URL and navigate directly (let route watcher handle the rest)
    const urlParams = buildUrlFromFilter(tempFilter);
    const newUrl = `/workorder/list${urlParams ? '?' + urlParams : ''}`;
    
    console.log('setFilter navigating to:', newUrl);
    router.replace(newUrl);
};

const refresh = () => {
    Emitter.emit('datatable-refresh', props.embedded ? 'workorder-embedded' : 'workorder-list');
};

const newRecord = () => {
    router.push({
        path: "/workorder/edit/new"
    });
};

const editRecord = (id: any) => {
    router.push({
        path: "/workorder/edit/" + id
    });
};

const editContact = (id?: number) => {
    if (id) {
        Emitter.emit('open:clientForm', id);
    }
};

const deleteRecord = async (id: number) => {
    actionDropdown.value = {};
    
    const confirmed = window.confirm('Are you sure you want to delete this work order? This action cannot be undone.');

    if (confirmed) {
        try {
            const success = await Api.deleteWorkOrder(id);
            if (success) {
                Alert.notify('Work order deleted successfully');
                refresh();
            } else {
                Alert.notify('Failed to delete work order');
            }
        } catch (error) {
            console.error('Error deleting work order:', error);
            Alert.notify('Error deleting work order');
        }
    }
};

const printInvoice = async (workOrder: WorkOrderModel) => {
    actionDropdown.value = {};
    
    if (!workOrder.Id) {
        Alert.notify({ text: 'Cannot print invoice for unsaved work order', type: 'error' });
        return;
    }
    
    try {
        // Always load the full work order record from API to ensure all data is present
        // (Datatable rows may have incomplete data for PDF generation)
        const fullWorkOrder = await Api.loadWorkOrder(workOrder.Id);
        
        InvoicePdfService.generateInvoicePDF(fullWorkOrder);
        Alert.notify({ text: 'Invoice PDF generated', type: 'success' });
    } catch (error) {
        console.error('Error generating invoice:', error);
        Alert.notify({ text: 'Failed to generate invoice PDF', type: 'error' });
    }
};

const getStatusLabel = (status?: number | null): string => {
    const found = statusOptions.find(opt => opt.value === status);
    return found?.label || 'Unknown';
};

const getStatusBadgeClass = (status?: number | null): string => {
    switch (status) {
        case 0: return 'bg-secondary';
        case 1: return 'bg-warning';
        case 2: return 'bg-info';
        case 3: return 'bg-success';
        case 4: return 'bg-danger';
        default: return 'bg-secondary';
    }
};

const getRowClass = (row: any): string => {
    const status = row.Status;
    switch (status) {
        case 0: return 'row-status-draft';      // Draft
        case 1: return 'row-status-pending';    // Pending
        case 2: return 'row-status-progress';   // Approved (in progress)
        case 3: return 'row-status-completed';  // Completed
        case 4: return 'row-status-cancelled';  // Cancelled
        default: return '';
    }
};

// ===== FILTER BADGE FUNCTIONS =====

// Computed property to check if any filters are active
const hasActiveFilters = computed(() => {
    return currentFilter.value.client.length > 0 ||
           currentFilter.value.status.length > 0 ||
           currentFilter.value.startDate ||
           currentFilter.value.endDate;
});

// Helper function to get client name
const getClientName = (customerId: number) => {
    const client = ClientList.value.find((c: any) => c.CustomerNumber === customerId);
    return client?.Company || `Client #${customerId}`;
};

// Helper function to get status name
const getStatusName = (statusId: number) => {
    return getStatusLabel(statusId);
};

// Remove individual client filter
const removeClientFilter = (clientId: number) => {
    const updatedClients = currentFilter.value.client.filter(c => c !== clientId);
    setFilter({ client: updatedClients });
};

// Remove individual status filter
const removeStatusFilter = (statusId: number) => {
    const updatedStatuses = currentFilter.value.status.filter(s => s !== statusId);
    setFilter({ status: updatedStatuses });
};

// Remove date filter
const removeDateFilter = () => {
    setFilter({ startDate: '', endDate: '' });
};

// Clear all filters
const clearAllFilters = () => {
    setFilter({
        client: [],
        status: [],
        startDate: '',
        endDate: ''
    });
};

// Format date range for display
const formatDateRange = () => {
    if (currentFilter.value.startDate && currentFilter.value.endDate) {
        return `${currentFilter.value.startDate} to ${currentFilter.value.endDate}`;
    } else if (currentFilter.value.startDate) {
        return `From ${currentFilter.value.startDate}`;
    } else if (currentFilter.value.endDate) {
        return `Until ${currentFilter.value.endDate}`;
    }
    return '';
};

// ===== SELECTION MANAGEMENT =====

const toggleRowSelection = (rowId: number) => {
    const index = selectedRows.value.indexOf(rowId);
    if (index > -1) {
        selectedRows.value.splice(index, 1);
    } else {
        selectedRows.value.push(rowId);
    }
    updateSelectAllState();
};

const toggleSelectAll = () => {
    if (selectAll.value) {
        selectedRows.value = [];
        selectAll.value = false;
    } else {
        selectedRows.value = rows.value.map((row: any) => row.Id);
        selectAll.value = true;
    }
};

const updateSelectAllState = () => {
    if (rows.value.length === 0) {
        selectAll.value = false;
        return;
    }
    const allSelected = rows.value.every((row: any) => selectedRows.value.includes(row.Id));
    selectAll.value = allSelected;
};

const clearSelection = () => {
    selectedRows.value = [];
    selectAll.value = false;
};

// ===== BULK ACTIONS =====

const confirmBulkDelete = async () => {
    if (selectedRows.value.length === 0) return;
    
    try {
        for (const id of selectedRows.value) {
            await Api.deleteWorkOrder(id);
        }
        Alert.notify({ text: `Successfully deleted ${selectedRows.value.length} work order(s)`, type: 'success' });
        clearSelection();
        refresh();
    } catch (error) {
        console.error('Error deleting work orders:', error);
        Alert.notify({ text: 'Failed to delete some work orders', type: 'error' });
    }
    
    showDeleteModal.value = false;
};

// Direct data loading function (bypasses datatable event system)
const loadData = async () => {
    console.debug('loadData called with currentFilter:', currentFilter.value);
    
    // Call handleDatatableUpdate with current datatable state
    await handleDatatableUpdate({
        page: currentFilter.value.page,
        size: currentFilter.value.size,
        sort: currentFilter.value.sort
    });
};

// Watch route changes to update filter
if (!props.embedded) {
    watch(() => route.query, (newQuery) => {
        console.debug('=== ROUTE WATCHER TRIGGERED ===');
        console.debug('Route query changed:', newQuery);
        
        // Parse new filter from URL
        const newFilter = parseUrlToFilter();
        
        // Only update if filter actually changed (avoid infinite loops)
        const currentParams = buildUrlFromFilter(currentFilter.value);
        const newParams = buildUrlFromFilter(newFilter);
        
        console.debug('Current params:', currentParams);
        console.debug('New params:', newParams);
        
        if (currentParams !== newParams) {
            console.debug('✅ Filter changed, updating currentFilter and calling loadData');
            currentFilter.value = newFilter;
            
            // Update cache with new filter state
            Cache.set('workorder-list-filter', currentFilter.value);
            
            // Directly load data with new filters instead of using refresh event
            loadData();
        } else {
            console.debug('❌ Filter unchanged, skipping update');
        }
    }, { immediate: false });
}

onMounted(() => {
    console.debug('WorkOrderDatatable mounted, initializing...');
    if (!props.embedded) {
        initializeFilter();
    }
    refresh();
});
</script>

<style scoped>
/* Datatable link hover effect - blue theme with edit icon badge */
:deep(td a) {
    text-decoration: none;
    color: #333;
    padding: 2px 6px;
    border-radius: 4px;
    border: 1px solid transparent;
    transition: all 0.2s ease-in-out;
    display: inline-block;
    position: relative;
}

:deep(td a:hover) {
    background-color: #15486A;
    color: #fff;
}

/* Edit icon badge in top-right corner on hover */
:deep(td a:hover::after) {
    content: '\f044';
    font-family: 'Font Awesome 5 Free';
    font-weight: 900;
    position: absolute;
    top: -6px;
    right: -6px;
    background: #3B9796;
    color: white;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    border: 2px solid white;
}
</style>
